import React, { useRef, useState } from 'react';
import { Camera, Images, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { submitOpsTaskReport, type OpsTaskListItem, type SubmitReportInput } from '@/api/opsTasks';
import { useT } from '@/contexts/I18nContext';

interface Props {
  task: OpsTaskListItem;
  initialFiles?: File[];
  onClose: () => void;
  onSubmitted: () => void;
}

export function SubmitReportDialog({ task, initialFiles = [], onClose, onSubmitted }: Props) {
  const { t } = useT();
  const [text, setText] = useState('');
  const [mediaFiles, setMediaFiles] = useState<File[]>(initialFiles);
  const [previews, setPreviews] = useState<string[]>(() =>
    initialFiles.map(f => URL.createObjectURL(f)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Camera input: single shot only — `capture` is honoured by the OS ONLY when
  // `multiple` is absent, so this opens the rear camera directly. Users add
  // several shots by tapping "Сделать фото" again (each adds one to the list).
  const cameraRef = useRef<HTMLInputElement>(null);
  // Gallery input: multi-select from the device's existing media, no capture.
  const galleryRef = useRef<HTMLInputElement>(null);

  const requiresPhoto = (task.answer_format ?? '').includes('photo');
  const requiresText = (task.answer_format ?? '').includes('text');

  function addFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const newFiles = Array.from(files);
    const newPreviews = newFiles.map(f => URL.createObjectURL(f));
    setMediaFiles(prev => [...prev, ...newFiles]);
    setPreviews(prev => [...prev, ...newPreviews]);
  }

  function removeFile(idx: number) {
    URL.revokeObjectURL(previews[idx]);
    setMediaFiles(prev => prev.filter((_, i) => i !== idx));
    setPreviews(prev => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const photos = mediaFiles.filter(f => f.type.startsWith('image/'));
      const videos = mediaFiles.filter(f => f.type.startsWith('video/'));
      const input: SubmitReportInput = { text: text.trim(), photos, videos, close: true };
      await submitOpsTaskReport(task.id, input);
      onSubmitted();
    } catch (e) {
      const message = (e as { response?: { data?: { detail?: string } } }).response?.data?.detail;
      setError(message || t('common.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tasks.submitReportTitle')}</DialogTitle>
          <DialogDescription>{task.title}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {(requiresText || !requiresPhoto) && (
            <div className="grid gap-1.5">
              <Label htmlFor="report-text">{t('tasks.reportText')}</Label>
              <Textarea
                id="report-text"
                value={text}
                onChange={e => setText(e.target.value)}
                rows={3}
                placeholder={t('tasks.reportTextPlaceholder')}
              />
            </div>
          )}

          {previews.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {previews.map((src, i) => {
                const isVideo = mediaFiles[i]?.type.startsWith('video/');
                return (
                  <div key={src} className="relative size-16 shrink-0">
                    {isVideo ? (
                      <video
                        src={src}
                        className="size-full rounded-md border border-border object-cover"
                      />
                    ) : (
                      <img
                        src={src}
                        alt=""
                        className="size-full rounded-md border border-border object-cover"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="absolute -right-1.5 -top-1.5 rounded-full bg-destructive p-0.5 text-white"
                      aria-label="Remove"
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Camera: single shot (no `multiple`, so the OS honours `capture` and
              opens the camera). Tap again to add more shots one-by-one. */}
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={e => {
              addFiles(e.target.files);
              if (cameraRef.current) cameraRef.current.value = '';
            }}
          />
          {/* Gallery: multi-select existing photos/videos (no capture). */}
          <input
            ref={galleryRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={e => {
              addFiles(e.target.files);
              if (galleryRef.current) galleryRef.current.value = '';
            }}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => cameraRef.current?.click()}
              className="gap-1.5"
            >
              <Camera className="size-4" />
              {t('tasks.takePhoto')}
              {requiresPhoto && previews.length === 0 ? ' *' : ''}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => galleryRef.current?.click()}
              className="gap-1.5"
            >
              <Images className="size-4" />
              {t('tasks.chooseFromGallery')}
            </Button>
          </div>

          {error && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : t('tasks.submitReport')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
